import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { CANONICAL_ALIAS, PUBLIC_PAGES } from './SiteMetadata';
import { ANALYTICS_CHOICE_KEY as KEY, GA_ID, VALID_GA_ID as VALID_ID, trackPublicEvent, syncPublicAnalytics } from '@/lib/public-analytics';

export function openCookiePreferences() { window.dispatchEvent(new Event('ca:open-cookie-preferences')); }

function readChoice() {
  try { const choice = window.localStorage.getItem(KEY); return choice === 'yes' || choice === 'no' ? choice : null; }
  catch { return null; }
}

function clearAnalyticsCookies() {
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.trim().split('=')[0];
    if (!/^_ga(?:_|$)/.test(name)) continue;
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.cainversionesservicios.com; SameSite=Lax`;
  }
}

export default function CookieConsent() {
  const { pathname } = useLocation();
  const [choice, setChoice] = useState(readChoice);
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => {
    const open = () => setSettingsOpen(true);
    const storage = event => { if (event.key === KEY || event.key === null) setChoice(readChoice()); };
    window.addEventListener('ca:open-cookie-preferences', open);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener('ca:open-cookie-preferences', open);
      window.removeEventListener('storage', storage);
    };
  }, []);
  const canonicalPath = CANONICAL_ALIAS[pathname] || pathname;
  const publicPage = Boolean(PUBLIC_PAGES[canonicalPath]);

  useEffect(() => {
    syncPublicAnalytics(choice, pathname);
  }, [choice, pathname, canonicalPath, publicPage]);

  useEffect(() => {
    if (!publicPage || choice !== 'yes' || !VALID_ID || Capacitor.isNativePlatform()) return;
    function click(event) {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!anchor) return;
      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.protocol === 'tel:') trackPublicEvent('click_phone');
        else if (url.protocol === 'https:' && url.hostname === 'wa.me') trackPublicEvent('click_whatsapp');
      } catch { /* Invalid destination: no analytics event. */ }
    }
    document.addEventListener('click', click, true);
    return () => document.removeEventListener('click', click, true);
  }, [choice, publicPage]);

  if (Capacitor.isNativePlatform() || !publicPage) return null;
  const save = value => {
    try { window.localStorage.setItem(KEY, value); setChoice(value); }
    catch { setChoice('no'); }
    if (value === 'no') { if (VALID_ID) window[`ga-disable-${GA_ID}`] = true; window.gtag?.('consent', 'update', { analytics_storage: 'denied' }); clearAnalyticsCookies(); }
    setSettingsOpen(false);
  };
  return <>
    {choice === null && <section role="region" aria-label="Preferencias de cookies" className="fixed bottom-3 left-3 right-3 z-[90] max-w-xl rounded-xl border border-neutral-600 bg-[#161616] p-4 text-sm text-white shadow-2xl sm:left-auto sm:right-5" data-testid="cookie-banner">
      <p className="font-semibold">Tu privacidad en C&A</p>
      <p className="mt-1 text-neutral-200">Usamos almacenamiento necesario para tu sesión. Las estadísticas de uso del sitio son opcionales y solo se activan si las aceptas. <Link className="underline" to="/privacy">Política de privacidad</Link>.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => save('no')} className="min-h-11 rounded-lg border border-neutral-500 px-4 hover:bg-neutral-800">Solo necesarias</button>
        <button type="button" onClick={() => save('yes')} className="min-h-11 rounded-lg bg-[#BD1E2D] px-4 font-semibold hover:bg-[#A11524]">Aceptar analíticas</button>
      </div>
    </section>}
    {choice !== null && <button type="button" onClick={() => setSettingsOpen(open => !open)} className="fixed bottom-3 left-3 z-[90] rounded-lg border border-neutral-600 bg-[#161616] px-3 py-2 text-xs text-white shadow-lg" aria-label="Cambiar preferencias de cookies" aria-expanded={settingsOpen} aria-controls="ca-cookie-settings">Cookies</button>}
    {settingsOpen && <section id="ca-cookie-settings" role="region" aria-label="Preferencias de cookies" className="fixed bottom-14 left-3 right-3 z-[90] max-w-md rounded-xl border border-neutral-600 bg-[#161616] p-5 text-white shadow-2xl">
      <div>
        <h2 className="text-lg font-semibold">Preferencias de cookies</h2>
        <p className="mt-2 text-sm text-neutral-200">Puedes cambiar tu elección en cualquier momento. Las funciones necesarias para iniciar sesión siguen disponibles.</p>
        <div className="mt-4 flex flex-wrap gap-2"><button type="button" onClick={() => save('no')} className="min-h-11 rounded-lg border border-neutral-500 px-4">Solo necesarias</button><button type="button" onClick={() => save('yes')} className="min-h-11 rounded-lg bg-[#BD1E2D] px-4">Aceptar analíticas</button><button type="button" onClick={() => setSettingsOpen(false)} className="min-h-11 px-3 underline">Cerrar</button></div>
      </div>
    </section>}
  </>;
}

